import { QueryClient } from '@tanstack/angular-query-experimental';
import {
  ActionDto,
  ActionReferenceDto,
  PrePopulatedValue,
  SegmentDto,
  StepDto,
  WorkflowDefinitionDto,
} from 'workflow-definition/openapi';

// The definition payload carries `references` per action (computed by the backend). Saving an action
// property afterwards only patches other caches, so the cached definition would keep the references it
// was loaded with and the reorder warning could not see a reference added in this session. These helpers
// recompute an action's references from the values just saved, mirroring the backend's property rules,
// so the cached definition stays accurate without refetching it.

export type StoredIdKind = 'RefId' | 'PrimaryKey';
export type ToRefId = (id: number, stored: StoredIdKind) => number;
export type PropertyValues = Record<string, string | null | undefined>;

export interface ReferenceUpdate {
  /** Full property map of the action. Left out when it is not known, which keeps the current property references. */
  properties?: PropertyValues | null;
  /** Pre-populate rows as saved. Left out when not known, which keeps the current pre-populate references. */
  prePopulatedValues?: PrePopulatedValue[] | null;
}

const SEND_LIST_CODES = [
  'actiontosend',
  'actionids',
  'actionids1',
  'actionids2',
];

const TRIGGER_TYPE_CODE_BY_ID_CODE: Record<string, string> = {
  triggerid: 'triggertype',
  starttriggerid: 'starttriggertype',
  stoptriggerid: 'stoptriggertype',
};

const PROPERTY_SOURCES = ['Label', 'ScanParams', 'SendList', 'Trigger'];

const LABEL_ELEMENT = /<action\s+id\s*=\s*["']?(\d+)["']?\s*>.*?<\/action>/gis;

function toId(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
}

function parseArray(text: string): unknown[] | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function extractPropertyReferences(
  properties: PropertyValues,
  toRefId: ToRefId
): ActionReferenceDto[] {
  const found: ActionReferenceDto[] = [];
  const lowerCased = new Map<string, string>();
  Object.entries(properties).forEach(([code, value]) =>
    lowerCased.set(code.toLowerCase(), value ?? '')
  );

  Object.entries(properties).forEach(([propertyCode, raw]) => {
    const text = raw ?? '';
    if (!text.trim()) return;
    const code = propertyCode.toLowerCase();

    if (code === 'label') {
      for (const match of text.matchAll(LABEL_ELEMENT)) {
        const id = toId(match[1]);
        if (id !== null)
          found.push({
            kind: 'Action',
            refId: toRefId(id, 'RefId'),
            source: 'Label',
            propertyCode,
          });
      }
    } else if (code.startsWith('scanparams')) {
      parseArray(text)?.forEach(entry => {
        const item = entry as { source?: unknown; value?: unknown } | null;
        if (String(item?.source).toUpperCase() !== 'WORKFLOW_COMPONENT') return;
        const id = toId(item?.value);
        if (id !== null)
          found.push({
            kind: 'Action',
            refId: toRefId(id, 'PrimaryKey'),
            source: 'ScanParams',
            propertyCode,
          });
      });
    } else if (SEND_LIST_CODES.includes(code)) {
      parseArray(text)?.forEach(entry => {
        const id = toId(entry);
        if (id !== null)
          found.push({
            kind: 'Action',
            refId: toRefId(id, 'RefId'),
            source: 'SendList',
            propertyCode,
          });
      });
    } else if (TRIGGER_TYPE_CODE_BY_ID_CODE[code]) {
      const triggerType = lowerCased
        .get(TRIGGER_TYPE_CODE_BY_ID_CODE[code])
        ?.trim()
        .toUpperCase();
      const kind =
        triggerType === 'GEOTRI' || triggerType === 'BUTTRI'
          ? 'Action'
          : triggerType === 'STEPCOMPL'
            ? 'Step'
            : triggerType === 'SGMTCOMPL'
              ? 'Segment'
              : null;
      const id = toId(text.trim());
      if (kind && id !== null)
        found.push({ kind, refId: id, source: 'Trigger', propertyCode });
    }
  });

  return found;
}

export function extractPrePopulatedReferences(
  values: PrePopulatedValue[] | null | undefined
): ActionReferenceDto[] {
  return (values ?? [])
    .filter(
      value => value.type === 'WFComp' && (value.sourceActionRefId ?? 0) > 0
    )
    .map(
      (value): ActionReferenceDto => ({
        kind: 'Action',
        refId: value.sourceActionRefId as number,
        source: 'PrePopulate',
      })
    );
}

/** Replaces the references the update knows about and keeps the rest. */
export function mergeActionReferences(
  previous: ActionReferenceDto[] | null | undefined,
  update: ReferenceUpdate,
  toRefId: ToRefId
): ActionReferenceDto[] {
  const kept = (previous ?? []).filter(reference => {
    const fromProperty = PROPERTY_SOURCES.includes(reference.source ?? '');
    if (update.properties && fromProperty) return false;
    if (update.prePopulatedValues && reference.source === 'PrePopulate')
      return false;
    return true;
  });

  return [
    ...kept,
    ...(update.properties
      ? extractPropertyReferences(update.properties, toRefId)
      : []),
    ...(update.prePopulatedValues
      ? extractPrePopulatedReferences(update.prePopulatedValues)
      : []),
  ];
}

/**
 * Converts an action id found in a property value to a RefId using the actions of the loaded workflow:
 * the kind the property is expected to hold wins, the other kind is the fallback.
 */
export function actionIdConverter(
  workflow: WorkflowDefinitionDto | undefined
): ToRefId {
  const refIds = new Set<number>();
  const refIdByPrimaryKey = new Map<number, number>();
  workflow?.segments?.forEach(segment =>
    segment.steps?.forEach(step =>
      step.actions?.forEach(action => {
        const refId = toId(action.id);
        const primaryKey = toId(action.actionId);
        if (refId !== null) refIds.add(refId);
        if (refId !== null && primaryKey !== null)
          refIdByPrimaryKey.set(primaryKey, refId);
      })
    )
  );

  return (id, stored) => {
    if (stored === 'RefId' && refIds.has(id)) return id;
    return refIdByPrimaryKey.get(id) ?? id;
  };
}

/** Patches the cached definition (workflow and segment views) after an action was saved. */
export function updateCachedActionReferences(
  queryClient: QueryClient,
  target: {
    workflowId: number;
    segmentId: number;
    stepId: number;
    actionId: number;
  },
  update: ReferenceUpdate
): void {
  const toRefId = actionIdConverter(
    queryClient.getQueryData<WorkflowDefinitionDto>([
      'Workflow',
      target.workflowId,
    ])
  );

  const patchSteps = (steps: StepDto[] | null | undefined) =>
    steps?.map(step =>
      step.id === target.stepId
        ? {
            ...step,
            actions: step.actions?.map((action: ActionDto) =>
              action.id === target.actionId
                ? {
                    ...action,
                    references: mergeActionReferences(
                      action.references,
                      update,
                      toRefId
                    ),
                  }
                : action
            ),
          }
        : step
    );

  queryClient.setQueryData<WorkflowDefinitionDto | undefined>(
    ['Workflow', target.workflowId],
    old =>
      old
        ? {
            ...old,
            segments: old.segments?.map(segment =>
              segment.id === target.segmentId
                ? { ...segment, steps: patchSteps(segment.steps) }
                : segment
            ),
          }
        : old
  );
  queryClient.setQueryData<SegmentDto | undefined>(
    ['Segment', target.segmentId],
    old => (old ? { ...old, steps: patchSteps(old.steps) } : old)
  );
}
